import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { Button } from '../components/ui/button';
import { ArrowRight, ChevronRight, Briefcase, Package, Users, Search } from 'lucide-react';
import axios from 'axios';
import SEO, { generateBreadcrumbSchema } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomePage = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [latestJobs, setLatestJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({ products: 40, active_jobs: 1, employees: 1 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({ products: [], suggestions: [], categories: [] });
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Helper function to get category name
  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    if (category) {
      return language === 'sv' ? category.name_sv : category.name_en;
    }
    return categoryId || '';
  };

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search with debounce
  useEffect(() => {
    if (searchQuery.length >= 2) {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          const res = await axios.get(`${API}/search?q=${encodeURIComponent(searchQuery)}`);
          setSearchResults(res.data);
          setShowSearchResults(true);
        } catch (error) {
          console.error('Search error:', error);
        }
      }, 300);
    } else {
      setSearchResults({ products: [], suggestions: [], categories: [] });
      setShowSearchResults(false);
    }
  }, [searchQuery]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/produkter?search=${encodeURIComponent(searchQuery)}`);
      setShowSearchResults(false);
      setSearchQuery('');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, jobsRes, statsRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/products?featured=true`),
          axios.get(`${API}/jobs`),
          axios.get(`${API}/stats`),
          axios.get(`${API}/categories`)
        ]);
        setFeaturedProducts(productsRes.data.slice(0, 4));
        setLatestJobs(jobsRes.data.slice(0, 3));
        setStats(statsRes.data);
        setCategories(categoriesRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div data-testid="home-page" className="bg-[#FAFAF8]">
      <SEO 
        title={language === 'sv' 
          ? 'Mätare, expansionssystem & tankar för VVS-proffs' 
          : 'Meters, Expansion Systems & Tanks for HVAC Professionals'}
        description={language === 'sv'
          ? 'Flowingo AB är Sveriges ledande B2B-leverantör av VVS-produkter. Specialister på mätare, expansionssystem och tankar. Komplett sortiment av pumpar, ventiler, värmeväxlare, återströmningsskydd och automatisering. Begär offert idag!'
          : 'Flowingo AB is Sweden\'s leading B2B supplier of HVAC products. Specialists in meters, expansion systems and tanks. Complete range of pumps, valves, heat exchangers and automation. Request a quote today!'}
        language={language}
        schema={[{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          '@id': 'https://flowingo.se/#webpage',
          url: 'https://flowingo.se',
          name: 'Flowingo AB - Mätare, expansionssystem & tankar för VVS-proffs',
          description: 'Ledande B2B-leverantör av VVS-produkter. Specialister på mätare, expansionssystem och tankar.',
          isPartOf: { '@id': 'https://flowingo.se/#website' },
          about: { '@id': 'https://flowingo.se/#organization' },
          primaryImageOfPage: {
            '@type': 'ImageObject',
            url: 'https://flowingo.se/hero-image.jpg',
          },
          mainEntity: {
            '@type': 'ItemList',
            name: 'VVS-produktkategorier',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Mätare', url: 'https://flowingo.se/produkter/kategori/matare' },
              { '@type': 'ListItem', position: 2, name: 'Expansionssystem', url: 'https://flowingo.se/produkter/kategori/expansionssystem' },
              { '@type': 'ListItem', position: 3, name: 'Tankar', url: 'https://flowingo.se/produkter/kategori/tankar' },
              { '@type': 'ListItem', position: 4, name: 'Pumpar', url: 'https://flowingo.se/produkter/kategori/pumpar' },
              { '@type': 'ListItem', position: 5, name: 'Ventiler', url: 'https://flowingo.se/produkter/kategori/ventiler' },
              { '@type': 'ListItem', position: 6, name: 'Värmeväxlare', url: 'https://flowingo.se/produkter/kategori/varmevaxlare' },
              { '@type': 'ListItem', position: 7, name: 'Återströmningsskydd', url: 'https://flowingo.se/produkter/kategori/aterstromning' },
              { '@type': 'ListItem', position: 8, name: 'Luft- och partikelavskiljare', url: 'https://flowingo.se/produkter/kategori/luft-partikelavskiljare' },
              { '@type': 'ListItem', position: 9, name: 'Automatisering och manövrering', url: 'https://flowingo.se/produkter/kategori/automatisering-manovrering' },
            ]
          }
        }]}
      />
      {/* Hero Section - Zeekr Style */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Cinematic Background Video with Overlay */}
        <div className="absolute inset-0">
          <video
            data-testid="hero-video"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster="https://res.cloudinary.com/dxoiocxnw/video/upload/so_0,w_1920,q_auto/flowingo/branding/hero-wallpaper.jpg"
            className="w-full h-full object-cover hero-video-zoom"
          >
            <source src="https://res.cloudinary.com/dxoiocxnw/video/upload/f_webm,w_1920,q_auto/flowingo/branding/hero-wallpaper.webm" type="video/webm" />
            <source src="https://res.cloudinary.com/dxoiocxnw/video/upload/w_1920,q_auto/flowingo/branding/hero-wallpaper.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-stone-900/50 via-stone-900/20 via-70% to-[#FAFAF8]/40" />
          <div className="absolute inset-0 hero-vignette" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <p className="text-stone-800 text-sm uppercase tracking-widest mb-4 font-medium">
            {language === 'sv' ? 'Din pålitliga VVS-partner' : 'Your reliable HVAC partner'}
          </p>
          <h1 
            data-testid="hero-title"
            className="text-4xl sm:text-5xl lg:text-7xl font-bold text-stone-900 mb-8 tracking-tight leading-tight"
          >
            FLOWING CONCEPTS
          </h1>
          
          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-12 relative" ref={searchRef}>
            <form onSubmit={handleSearchSubmit} className="relative">
              <div className="flex items-center bg-white/10 backdrop-blur-sm border border-white/30 hover:bg-white/20 transition-all duration-300">
                <Search className="w-5 h-5 ml-4 text-white/70 shrink-0" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => searchQuery.length >= 2 && setShowSearchResults(true)}
                  placeholder={language === 'sv' ? 'Sök produkter, artikelnummer...' : 'Search products, article numbers...'}
                  className="w-full px-4 py-4 bg-transparent outline-none text-white placeholder-white/60"
                />
                <button 
                  type="submit"
                  className="px-8 py-4 bg-white/20 text-white font-medium hover:bg-white/30 transition-colors shrink-0 border-l border-white/30"
                >
                  {language === 'sv' ? 'Sök' : 'Search'}
                </button>
              </div>
            </form>

            {/* Search Results Dropdown */}
            {showSearchResults && (searchResults.products.length > 0 || searchResults.categories.length > 0 || searchResults.suggestions.length > 0) && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-stone-200 shadow-xl z-50 max-h-[60vh] overflow-auto text-left">
                <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-stone-200">
                  {/* Left side - Suggestions & Categories */}
                  <div className="p-4">
                    {searchResults.suggestions.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">
                          {language === 'sv' ? 'Sökförslag' : 'Suggestions'}
                        </h4>
                        <ul className="space-y-1">
                          {searchResults.suggestions.map((suggestion, idx) => (
                            <li key={idx}>
                              <button
                                onClick={() => {
                                  setSearchQuery(suggestion);
                                  navigate(`/produkter?search=${encodeURIComponent(suggestion)}`);
                                  setShowSearchResults(false);
                                }}
                                className="w-full text-left px-2 py-1.5 text-sm text-stone-700 hover:bg-stone-50 flex items-center gap-2"
                              >
                                <Search className="w-4 h-4 text-stone-400" />
                                {suggestion}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {searchResults.categories.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">
                          {language === 'sv' ? 'Kategorier' : 'Categories'}
                        </h4>
                        <ul className="space-y-1">
                          {searchResults.categories.map((cat) => (
                            <li key={cat.id}>
                              <Link
                                to={cat.slug ? (cat.parent_id ? `/produkter/kategori/${cat.parent_id}/${cat.slug}` : `/produkter/kategori/${cat.slug}`) : '/produkter'}
                                onClick={() => {
                                  setShowSearchResults(false);
                                  setSearchQuery('');
                                }}
                                className="block px-2 py-1.5 text-sm text-stone-700 hover:bg-stone-50"
                              >
                                {language === 'sv' ? cat.name_sv : cat.name_en}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Right side - Products */}
                  <div className="p-4">
                    <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">
                      {language === 'sv' ? 'Produkter' : 'Products'}
                    </h4>
                    <ul className="space-y-2">
                      {searchResults.products.slice(0, 5).map((product) => (
                        <li key={product.id}>
                          <Link
                            to={`/produkter/${product.slug || product.id}`}
                            onClick={() => {
                              setShowSearchResults(false);
                              setSearchQuery('');
                            }}
                            className="flex items-center gap-3 p-2 hover:bg-stone-50 transition-colors"
                          >
                            <div className="w-12 h-12 bg-stone-100 shrink-0 flex items-center justify-center">
                              {product.image_url ? (
                                <img 
                                  src={product.image_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${product.image_url}` : product.image_url} 
                                  alt="" 
                                  className="w-full h-full object-cover" 
                                />
                              ) : (
                                <Package className="w-5 h-5 text-stone-300" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-stone-900 truncate">
                                {language === 'sv' ? product.name_sv : product.name_en}
                              </p>
                              <p className="text-xs text-stone-500 truncate">
                                {product.main_category_id}
                              </p>
                            </div>
                          </Link>
                        </li>
                      ))}
                    </ul>
                    {searchResults.products.length > 5 && (
                      <button
                        onClick={() => {
                          navigate(`/produkter?search=${encodeURIComponent(searchQuery)}`);
                          setShowSearchResults(false);
                        }}
                        className="w-full mt-3 py-2 text-sm text-[#321a27] font-medium hover:bg-stone-50 border-t border-stone-200"
                      >
                        {language === 'sv' ? `Visa alla ${searchResults.products.length} resultat →` : `View all ${searchResults.products.length} results →`}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/produkter">
              <Button 
                size="lg" 
                data-testid="hero-cta-products"
                className="bg-stone-900 text-white hover:bg-stone-800 px-8 py-6 text-base font-medium rounded-none"
              >
                {t('hero.cta')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/jobb">
              <Button 
                variant="outline" 
                size="lg"
                data-testid="hero-cta-jobs"
                className="border-stone-900 text-stone-900 hover:bg-stone-900 hover:text-white px-8 py-6 text-base font-medium rounded-none"
              >
                {t('hero.jobsCta')}
              </Button>
            </Link>
          </div>
        </div>

      </section>

      {/* Stats Section */}
      <section className="py-20 bg-white border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8" data-testid="stat-products">
              <Package className="w-10 h-10 text-stone-400 mx-auto mb-4" />
              <div className="text-4xl font-bold text-stone-900 mb-2">{stats.products}+</div>
              <div className="text-stone-500 uppercase tracking-wider text-sm">
                {language === 'sv' ? 'Produkter' : 'Products'}
              </div>
            </div>
            <div className="text-center p-8" data-testid="stat-jobs">
              <Briefcase className="w-10 h-10 text-stone-400 mx-auto mb-4" />
              <div className="text-4xl font-bold text-stone-900 mb-2">{stats.active_jobs}</div>
              <div className="text-stone-500 uppercase tracking-wider text-sm">
                {language === 'sv' ? 'Lediga tjänster' : 'Open positions'}
              </div>
            </div>
            <div className="text-center p-8" data-testid="stat-team">
              <Users className="w-10 h-10 text-stone-400 mx-auto mb-4" />
              <div className="text-4xl font-bold text-stone-900 mb-2">{stats.employees}</div>
              <div className="text-stone-500 uppercase tracking-wider text-sm">
                {language === 'sv' ? 'Teammedlemmar' : 'Team members'}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-stone-500 text-sm uppercase tracking-widest mb-2">
              {language === 'sv' ? 'Varför välja oss' : 'Why choose us'}
            </p>
            <h2 className="text-2xl sm:text-3xl font-bold text-stone-900">
              {language === 'sv' ? 'Din pålitliga VVS-partner' : 'Your reliable HVAC partner'}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8 bg-white border border-stone-200">
              <div className="w-16 h-16 bg-[#321a27] text-white flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-stone-900 mb-3">
                {language === 'sv' ? 'Kvalitetsgaranti' : 'Quality Guarantee'}
              </h3>
              <p className="text-stone-600">
                {language === 'sv' 
                  ? 'Alla våra produkter uppfyller högsta kvalitetsstandarder och levereras med fullständig garanti.'
                  : 'All our products meet the highest quality standards and come with full warranty.'
                }
              </p>
            </div>
            <div className="text-center p-8 bg-white border border-stone-200">
              <div className="w-16 h-16 bg-[#321a27] text-white flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-stone-900 mb-3">
                {language === 'sv' ? 'Snabb leverans' : 'Fast Delivery'}
              </h3>
              <p className="text-stone-600">
                {language === 'sv' 
                  ? 'Effektiv lagerhantering och logistik garanterar snabba leveranser till ditt projekt.'
                  : 'Efficient inventory management and logistics ensure fast deliveries to your project.'
                }
              </p>
            </div>
            <div className="text-center p-8 bg-white border border-stone-200">
              <div className="w-16 h-16 bg-[#321a27] text-white flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-stone-900 mb-3">
                {language === 'sv' ? 'Teknisk support' : 'Technical Support'}
              </h3>
              <p className="text-stone-600">
                {language === 'sv' 
                  ? 'Vårt erfarna team står redo att hjälpa dig med teknisk rådgivning och support.'
                  : 'Our experienced team is ready to help you with technical advice and support.'
                }
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      {featuredProducts.length > 0 && (
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-12">
              <div>
                <p className="text-stone-500 text-sm uppercase tracking-widest mb-2">
                  {language === 'sv' ? 'Populära produkter' : 'Popular products'}
                </p>
                <h2 className="text-2xl sm:text-3xl font-bold text-stone-900">
                  {language === 'sv' ? 'Mest efterfrågade just nu' : 'Most requested right now'}
                </h2>
              </div>
              <Link to="/produkter" className="text-stone-600 hover:text-stone-900 flex items-center gap-2 text-sm font-medium">
                {t('common.seeAll')} <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredProducts.slice(0, 4).map((product) => (
                <Link 
                  key={product.id}
                  to={`/produkter/${product.slug || product.id}`}
                  data-testid={`popular-product-${product.id}`}
                  className="group bg-[#FAFAF8] overflow-hidden border border-stone-200 hover:border-stone-300 transition-all duration-300"
                >
                  <div className="aspect-video bg-stone-100 overflow-hidden">
                    {product.image_url ? (
                      <img 
                        src={product.image_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${product.image_url}` : product.image_url}
                        alt={language === 'sv' ? product.name_sv : product.name_en}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-stone-400">
                        <Package className="w-12 h-12" />
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="text-xs text-stone-500 uppercase tracking-wider mb-2">
                      {getCategoryName(product.main_category_id)}
                    </div>
                    <h3 className="text-base font-semibold text-stone-900 mb-2 group-hover:text-stone-700 line-clamp-2">
                      {language === 'sv' ? product.name_sv : product.name_en}
                    </h3>
                    <p className="text-stone-600 text-sm line-clamp-2">
                      {language === 'sv' ? product.description_sv : product.description_en}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Latest Jobs */}
      {latestJobs.length > 0 && (
        <section className="py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-12">
              <div>
                <p className="text-stone-500 text-sm uppercase tracking-widest mb-2">
                  {language === 'sv' ? 'Karriär hos Flowingo' : 'Careers at Flowingo'}
                </p>
                <h2 className="text-2xl sm:text-3xl font-bold text-stone-900">
                  {language === 'sv' ? 'Senaste jobben' : 'Latest Jobs'}
                </h2>
              </div>
              <Link to="/jobb" className="text-stone-600 hover:text-stone-900 flex items-center gap-2 text-sm font-medium">
                {t('common.seeAll')} <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="space-y-4">
              {latestJobs.map((job) => (
                <Link 
                  key={job.id}
                  to={`/jobs/${job.id}`}
                  data-testid={`job-card-${job.id}`}
                  className="block bg-white p-6 border border-stone-200 hover:border-stone-300 hover:shadow-sm transition-all duration-300"
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-stone-900">
                          {language === 'sv' ? job.title_sv : job.title_en}
                        </h3>
                        <span className="px-2 py-1 text-xs bg-stone-100 text-stone-600 rounded">
                          {t(`roleTypes.${job.role_type}`)}
                        </span>
                      </div>
                      <p className="text-stone-600 text-sm">
                        {job.location} • {t(`experienceLevels.${job.experience_level}`)}
                      </p>
                    </div>
                    <Button 
                      variant="outline" 
                      className="border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white hover:border-stone-900 shrink-0 rounded-none"
                    >
                      {t('jobs.apply')} <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-24 bg-stone-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-stone-400 text-sm uppercase tracking-widest mb-4">
            {language === 'sv' ? 'Kontakta oss' : 'Get in touch'}
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            {language === 'sv' ? 'Redo att samarbeta?' : 'Ready to collaborate?'}
          </h2>
          <p className="text-stone-400 text-lg mb-8 max-w-2xl mx-auto">
            {language === 'sv' 
              ? 'Kontakta oss för att diskutera hur vi kan hjälpa ditt företag med VVS-lösningar.' 
              : 'Contact us to discuss how we can help your business with HVAC solutions.'
            }
          </p>
          <Link to="/kontakt">
            <Button 
              size="lg"
              data-testid="cta-contact"
              className="bg-white text-stone-900 hover:bg-stone-100 px-8 py-6 rounded-none"
            >
              {t('contact.title')} <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
